import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Clock from 'react-live-clock';
import tz_lookup from 'tz-lookup';
import { getI18n, useTranslation } from 'react-i18next';

import { useLazyGetPortQuery, useLazyGetWeatherByPortKeyQuery } from 'app/api/marine-infrastructure';

import { useAppDispatch, useAppSelector } from 'app/store/store';

import { useAlertHook } from 'app/hooks/useAlertHook';
import { glToStringHDMS, throttle, utc } from 'app/helpers';
import { InfoWrapper, WeatherBlock, Button, Icon } from 'app/components';
import { PortArrivals } from './PortArrivals';
import { PortVessels } from './PortVessels';
import { setFindPort as setFindPortDeckgl } from 'app/utils/selectedTargetsSlice';
import styles from './PortDetails.module.scss';
import { PortTerminals } from './PortTerminalsTable';
import { Tabs } from 'app/components/Tab/Tabs';
import { TabPanel } from 'app/components/Tab/TabPanel';
import { PortEvents } from './PortEvents';
import { CountryFlag } from 'CountryFlag/CountryFlag';

interface portCharacteristicI {
  label: string;
  value: string | undefined;
}
interface PortDetailsI {
  setInitCenter: React.Dispatch<React.SetStateAction<number[]>>
}

const filterOnlyYes = (prev: string[], cur: portCharacteristicI) => {
  if (cur.value === 'YES') {
    return prev.length > 0 ? [...prev, `, ${cur.label}`] : [...prev, cur.label];
  } else return prev;
};

export const PortDetails = ({ setInitCenter }: PortDetailsI) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { portKey } = useParams();

  const [getPort, { data: portData }] = useLazyGetPortQuery();
  const language = useMemo(() => getI18n().language, [t]);

  const profile = useAppSelector((state) => state.userSlice.profile);
  const timeFormat: string = useMemo(() => profile?.ui?.['timeFormat'] || 'HH:mm:ss', [profile]);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);

  useEffect(() => {
    if (portKey) {
      getPort(portKey);
    }
  }, [portKey, language]);

  const port = useMemo(() => portData?.port || null, [portData]);

  useEffect(() => {
    if (port && portKey) {
      debounceGetWeathersHandler(portKey);
    }
  }, [port, portKey]);

  const [getWeather, { data: portWeather }] = useLazyGetWeatherByPortKeyQuery();
  const debounceGetWeathersHandler = useCallback(throttle(getWeather, 500), []);

  const position: string | undefined = useMemo(() => {
    if (typeof port?.lat === 'number' && typeof port?.lon === 'number') {
      return glToStringHDMS([port.lon, port.lat], coordinatesFormat);
    }
  }, [port, coordinatesFormat]);

  const goToPort = useCallback(() => {
    dispatch(setFindPortDeckgl(port));
    navigate('/mapview');
  }, [port]);

  const [tabValue, setTabValue] = useState<string>();

  const handleChange = (event: React.SyntheticEvent, newValue: any) => {
    setTabValue(newValue);
  };

  const tabs = [
    {
      value: 'port-vessels',
      label: t('PortTargetCatalog'),
    },
    {
      value: 'port-arrivals',
      label: t('VesselCallsExpected'),
    },
    {
      value: 'port-events',
      label: t('PortEvents'),
    },
    {
      value: 'port-terminals',
      label: t('Terminals'),
    },
  ];

  const handleGoBack = () => {
    const local_center = localStorage.getItem('center');
    const parsed_local_center = local_center !== null ? JSON.parse(local_center) : [0, 0];
    setInitCenter([parsed_local_center[0], parsed_local_center[1]]);
    navigate(-1);
  }

  useEffect(() => {
    if (!tabValue) {
      setTabValue(tabs[0]?.value);
    }
  }, [tabs]);

  const entranceRestriction: string[] | undefined = [
    { label: t('RestrTide'), value: port?.characteristics?.restrTide },
    { label: t('RestrSwell'), value: port?.characteristics?.restrSwell },
    { label: t('RestrIce'), value: port?.characteristics?.restrIce },
    { label: t('RestrOther'), value: port?.characteristics?.restrOther },
  ].reduce<string[]>(filterOnlyYes, []);

  const wharvesAnchor: string[] | undefined = [
    { label: t('Wharve'), value: port?.characteristics?.wharve },
    { label: t('Anchorage'), value: port?.characteristics?.anchor },
    { label: t('DangCargoAncor'), value: port?.characteristics?.dangCargoAncor },
  ].reduce<string[]>(filterOnlyYes, []);

  const mooring: string[] | undefined = [
    { label: t('MedMoor'), value: port?.characteristics?.medMoor },
    { label: t('BeachMoor'), value: port?.characteristics?.beachMoor },
    { label: t('IceMoor'), value: port?.characteristics?.iceMoor },
  ].reduce<string[]>(filterOnlyYes, []);

  const cargoFacilities: string[] | undefined = [
    { label: t('RoRo'), value: port?.characteristics?.roro },
    { label: t('SolidBulk'), value: port?.characteristics?.solidBulk },
    { label: t('LiquidBulk'), value: port?.characteristics?.liquidBulk },
    { label: t('Contaiiner'), value: port?.characteristics?.contaiiner },
    { label: t('BreakBulk'), value: port?.characteristics?.breakBulk },
    { label: t('OilTerm'), value: port?.characteristics?.oilTerm },
    { label: t('LngTerm'), value: port?.characteristics?.lngTerm },
  ].reduce<string[]>(filterOnlyYes, []);

  const service: string[] | undefined = [
    { label: t('PortSecur'), value: port?.characteristics?.portSecur },
    { label: t('FirstPort'), value: port?.characteristics?.firstPort },
    { label: t('MedFacili'), value: port?.characteristics?.medFacili },
    { label: t('GarbageDisp'), value: port?.characteristics?.garbageDisp },
    { label: t('Cht'), value: port?.characteristics?.cht },
    { label: t('DirtyBall'), value: port?.characteristics?.dirtyBall },
  ].reduce<string[]>(filterOnlyYes, []);

  return (
    <div className={styles.page}>
      {port && (
        <>
          <div className={styles.heading_container}>
            <Button
              iconName="back"
              onClick={handleGoBack}
              disabled={history.state.idx === 0}
              variant="icon-only"
              iconSize="16px"
            />
            <Icon name="port" size="16" />
            <h2 className={styles.heading}>{port.name}</h2>
            {position && (
              <div className={styles.icon_target} onClick={goToPort}>
                <Icon name="aim" size="14px" color={'var(--default-primary)'} />
              </div>
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.sub_header}>{t('Port')}</div>
            <div className={styles.static}>
              <div className={styles.column}>
                <InfoWrapper
                  label={`${t('Country')}:`}
                  value={
                    typeof port.meta?.countryName !== 'undefined' ? (
                      <>
                        {port.countryCode && <CountryFlag countryCode={port.countryCode.toLowerCase()} />}
                        <strong>
                          {port.meta?.countryName} ({port.countryCode})
                        </strong>
                      </>
                    ) : undefined
                  }
                />
                <InfoWrapper
                  label="UN/LOCODE:"
                  strong
                  value={typeof port.locode !== undefined ? `${port.un} ${port.locode}` : undefined}
                />
                <InfoWrapper
                  label={`API ID:`}
                  value={
                    <div title={t('ClipboardCopy')} className={styles.copy_id}>
                      <span>{portKey}</span>
                      <Icon name="copy" size="14px" />
                    </div>
                  }
                  clickable
                  onClick={() => {
                    portKey &&
                      navigator.clipboard.writeText(portKey).then(() => {
                        addAlert({
                          data: {
                            messages: [{ description: t('ClipboardCopySuccess'), type: 'information' }],
                          },
                        });
                      });
                  }}
                />
              </div>
              <div className={styles.column}>
                <InfoWrapper
                  label={`${t('LocalTime')}:`}
                  value={
                    typeof port?.lat === 'number' && typeof port?.lon === 'number' ? (
                      <>
                        <Clock format={timeFormat} locale={'en'} ticking timezone={tz_lookup(port.lat, port.lon)} noSsr />
                        {`(UTC${utc(port.lat, port.lon)})`}
                      </>
                    ) : undefined
                  }
                />
                <InfoWrapper
                  clickable={!!position}
                  label={`${t('Coordinates')}:`}
                  value={position}
                  valueIcon={<Icon name="aim" size="14px" color={'var(--default-primary)'} />}
                  onClick={goToPort}
                />
              </div>
            </div>

            {portWeather?.weather && Object.keys(portWeather.weather)?.length > 0 && (
              <WeatherBlock
                weather={portWeather.weather}
                full
                utc={typeof port?.lat === 'number' && typeof port?.lon === 'number' ? `(UTC${utc(port.lat, port.lon)})` : null}
              />
            )}
            <div className={styles.port_tables}>
              <Tabs tabs={tabs} value={tabValue} onChange={handleChange}></Tabs>

              <TabPanel id="port-vessels" value={tabValue}>
                <div className={styles.ship_calls}>
                  <PortVessels />
                </div>
              </TabPanel>

              <TabPanel id="port-arrivals" value={tabValue}>
                <div className={styles.ship_calls}>
                  <PortArrivals />
                </div>
              </TabPanel>
              <TabPanel id="port-events" value={tabValue}>
                <div className={styles.ship_calls}>
                  <PortEvents />
                </div>
              </TabPanel>
              <TabPanel id="port-terminals" value={tabValue}>
                <div className={styles.ship_calls}>
                  <PortTerminals />
                </div>
              </TabPanel>
            </div>

            <div>
              <div className={styles.static}>
                <div className={styles.column}>
                  <InfoWrapper label={`${t('HarborSize')}`} value={port.characteristics?.harborSize} />
                  <InfoWrapper label={`${t('HarborType')}`} value={port.characteristics?.harborType} />
                </div>
                <div className={styles.column}>
                  <InfoWrapper label={`${t('HarborUse')}`} value={port.characteristics?.harborUse} />
                </div>
              </div>

              <div className={styles.static}>
                <div className={styles.column}>
                  <InfoWrapper
                    label={`${t('TidalRange')}`}
                    value={
                      typeof port.characteristics?.tidalRange === 'number'
                        ? port.characteristics?.tidalRange + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('EntranceWidth')}`}
                    value={
                      typeof port.characteristics?.entWidth === 'number'
                        ? port.characteristics?.entWidth + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('ChannelDepth')}`}
                    value={
                      typeof port.characteristics?.channelDepth === 'number'
                        ? port.characteristics?.channelDepth + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('AncDepth')}`}
                    value={
                      typeof port.characteristics?.ancDepth === 'number'
                        ? port.characteristics?.ancDepth + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('CargoDepth')}`}
                    value={
                      typeof port.characteristics?.cargoDepth === 'number'
                        ? port.characteristics?.cargoDepth + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('OilTermDepth')}`}
                    value={
                      typeof port.characteristics?.oilTermDepth === 'number'
                        ? port.characteristics?.oilTermDepth + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                </div>

                <div className={styles.column}>
                  <InfoWrapper
                    label={`${t('VesselLength')}`}
                    value={
                      typeof port.characteristics?.vesselLength === 'number'
                        ? port.characteristics?.vesselLength + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('VesselBeam')}`}
                    value={
                      typeof port.characteristics?.vesselBeam === 'number'
                        ? port.characteristics?.vesselBeam + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                  <InfoWrapper
                    label={`${t('VesselDraft')}`}
                    value={
                      typeof port.characteristics?.vesselDraft === 'number'
                        ? port.characteristics?.vesselDraft + '\u0020' + t('MeterUnit')
                        : undefined
                    }
                  />
                </div>
              </div>

              <InfoWrapper
                label={`${t('EntranceRestriction')}`}
                value={entranceRestriction?.length > 0 ? entranceRestriction : null}
              />
              <InfoWrapper label={`${t('WharvesAnchor')}`} value={wharvesAnchor.length > 0 ? wharvesAnchor : null} />
              <InfoWrapper label={`${t('Mooring')}`} value={mooring.length > 0 ? mooring : null} />
              <InfoWrapper label={`${t('Service')}`} value={service.length > 0 ? service : null} />
              <InfoWrapper label={`${t('CargoFacilities')}`} value={cargoFacilities.length > 0 ? cargoFacilities : null} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
